import { useEffect, useRef } from 'react';

import {
  DEFAULT_COMMUNE,
  DEFAULT_COMMUNE_NAME,
  DEFAULT_INTERNAL_COLLECT_ALIAS,
  DEFAULT_REGION,
  DEFAULT_REGION_NAME
} from '@/constants';
import type { Address } from '@/modules/cart/domain/Cart';
import { useStorageSession } from '@/modules/perseo-billing/hooks/use-storage-session';
import { useCreateShippingAddress } from '@/sections/address/hooks/mutators/useCreateShippingAddress';
import { useCreateShippingAddressCommune } from '@/sections/address/hooks/mutators/useCreateShippingAddressCommune';
import { useFindLocations } from '@/sections/region/hooks/useFindLocations';
import { isShippingAddressComplete } from '@/sections/shared/utils';

const useSetShippingAddress = (shippingAddress: Address | undefined) => {
  const shippingAddressRequestMade = useRef(false);
  const isRequestInProgress = useRef(false);

  const { trigger: createShippingAddress } = useCreateShippingAddress(false);
  const { trigger: createCommune } = useCreateShippingAddressCommune();
  const { user, updateUserSession } = useStorageSession();
  const { findCommuneName, findRegionName } = useFindLocations();

  useEffect(() => {
    if (
      shippingAddressRequestMade.current ||
      isRequestInProgress.current ||
      !shippingAddress
    ) {
      return;
    }

    let isMounted = true;
    isRequestInProgress.current = true;

    const handleInternalCollect = async () => {
      await createCommune({
        commune: DEFAULT_COMMUNE
      });

      updateUserSession({
        ...user,
        regionId: DEFAULT_REGION,
        regionName: DEFAULT_REGION_NAME,
        commune: DEFAULT_COMMUNE,
        communeName: DEFAULT_COMMUNE_NAME
      });
    };

    const handleCompleteShippingAddress = async (
      region: number,
      commune: string
    ) => {
      const newAddress: Address | Omit<Address, 'id'> = {
        ...shippingAddress,
        region: region,
        commune: commune
      };
      await createShippingAddress(newAddress);
    };

    const handleIncompleteShippingAddress = async (commune: string) => {
      await createCommune({
        commune: commune
      });
    };

    const updateLocalStorage = (region: number, commune: string) => {
      const regionName = findRegionName(region);
      const communeName = findCommuneName(region, commune);
      updateUserSession({
        ...user,
        regionId: region,
        regionName: regionName,
        commune: commune,
        communeName: communeName
      });
    };

    const createShippingAddressOnce = async () => {
      try {
        if (shippingAddress?.region === 0 || !shippingAddress?.commune) {
          return;
        }

        if (shippingAddress?.alias === DEFAULT_INTERNAL_COLLECT_ALIAS) {
          await handleInternalCollect();
        } else {
          const region = Number(shippingAddress?.region || DEFAULT_REGION);
          const commune = shippingAddress?.commune ?? DEFAULT_COMMUNE;
          if (isShippingAddressComplete(shippingAddress)) {
            await handleCompleteShippingAddress(region, commune);
          } else {
            await handleIncompleteShippingAddress(commune);
          }

          updateLocalStorage(region, commune);
        }

        if (isMounted) {
          shippingAddressRequestMade.current = true;
          isRequestInProgress.current = false;
        }
      } catch (error) {
        console.error('Error creating shipping address:', error);
        if (isMounted) {
          isRequestInProgress.current = false;
        }
      }
    };

    createShippingAddressOnce();

    return () => {
      isMounted = false;
    };
  }, [
    createCommune,
    createShippingAddress,
    findCommuneName,
    findRegionName,
    shippingAddress,
    updateUserSession,
    user
  ]);
};

export default useSetShippingAddress;
